import {RefObject, useEffect, useRef, useState} from 'react';
import {
  AriaSliderProps,
  mergeProps,
  useFocusRing,
  useNumberFormatter,
  useSlider,
  useSliderThumb,
  VisuallyHidden,
} from 'react-aria';
import {useTranslation} from 'react-i18next';
import {SliderState, useSliderState} from 'react-stately';

import {useGlobus} from '../GlobusProvider';

const OverlayMapTransparency = () => {
  const {t} = useTranslation();
  const numberFormatter = useNumberFormatter();
  const {overlayMap, setGlobus} = useGlobus();
  const trackRef = useRef<HTMLDivElement>(null);
  const [opacity, setOpacity] = useState(overlayMap?.opacity ?? 1);

  function mapOpacity(x: number): number {
    // Region 1: 1 → 0.8  (opacity 0.7 → 0.4)
    if (x === 1 ) {
      return 1;          
    }
    if (x >= 0.8) {
      const t = (x - 0.8) / 0.2;     // 0..1 from 0.8 → 1
      return 0.4 + t * 0.3;          // 0.4 → 0.7
    }

    // Region 2: 0.8 → 0.5 (opacity 0.4 → 0)
    if (x >= 0.5) {
      const t = (x - 0.5) / 0.3;     // 0..1 from 0.5 → 0.8
      return t * 0.4;                // 0 → 0.4
    }

    // Region 3: 0.5 → 0 (stay at 0)
    return 0;
  }


  useEffect(() => {
    setOpacity(overlayMap?.opacity ?? 1);
  }, []); // load only once

  useEffect(() => {
    setGlobus(globus => {
      if (globus.overlayMap != null) {
        globus.overlayMap.isVisible = true;

        const mapped = mapOpacity(opacity)
        const rounded = Math.round(mapped * 100) / 100;

        globus.overlayMap.opacity = rounded;
      }
    });
  }, [opacity, setGlobus]);

  const sliderProps: AriaSliderProps<number> = {
    minValue: 0,
    maxValue: 1,
    step: 0.01,
    value: opacity,
    onChange: setOpacity,
    orientation: 'horizontal',
    'aria-label': t('globus:description.transparency'),
  };

  const state = useSliderState({...sliderProps, numberFormatter});
  const {groupProps, trackProps} = useSlider(sliderProps, state, trackRef);

  return (
    <div {...groupProps} className="p-2">
      <div className="text-center">{t('globus:description.transparency')}</div>
      <div className="px-4 my-4 py-2 bg-diercke-blue-250 rounded-full" >
        <div {...trackProps} ref={trackRef} className="relative h-1 bg-diercke-blue-300 rounded-full">
          <div className="h-full bg-diercke-blue-400 rounded-full" style={{width: `${opacity * 100}%`}} />
          <Thumb state={state} trackRef={trackRef} />
        </div>
      </div>
    </div>
  );
};

export default OverlayMapTransparency;

type ThumbProps = {
  state: SliderState;
  trackRef: RefObject<Element>;
};

const Thumb: React.FC<ThumbProps> = ({state, trackRef}) => {
  const inputRef = useRef(null);
  const {thumbProps, inputProps, isDragging} = useSliderThumb(
    {
      index: 0,
      trackRef,
      inputRef,
    },
    state
  );
  const {focusProps, isFocusVisible} = useFocusRing();

  return (
    <div
      {...thumbProps}
      className={`
      top-1/2 w-8 h-8 rounded-full cursor-ew-resize
      border-diercke-blue-400 border-[2px]
      ${isFocusVisible || isDragging ? 'bg-diercke-blue-500' : 'bg-white'}
      `}
    >
      <VisuallyHidden>
        <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
      </VisuallyHidden>
    </div>
  );
};
